import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { Icon } from './Icon'

/* ---------- Buttons: purple pill (primary), translucent pill, text ---------- */

export const Button = ({
   children,
   onClick,
   variant = 'primary',
   size = 'md',
   disabled,
   className,
   type = 'button',
   testId,
   full,
   ariaLabel,
}: {
   children: ReactNode
   onClick?: () => void
   variant?: 'primary' | 'secondary' | 'ghost' | 'outline'
   size?: 'sm' | 'md' | 'lg'
   disabled?: boolean
   className?: string
   type?: 'button' | 'submit'
   testId?: string
   full?: boolean
   ariaLabel?: string
}) => (
   <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={ariaLabel}
      className={cn(
         'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all select-none whitespace-nowrap',
         size === 'sm' && 'h-9 px-4 text-[13px]',
         size === 'md' && 'h-11 px-6 text-[15px]',
         size === 'lg' && 'h-[52px] px-7 text-[16px]',
         variant === 'primary' &&
            'bg-[var(--ob-primary)] text-white hover:bg-[var(--ob-primary-dark)] shadow-[0_10px_24px_-12px_rgba(144,97,218,0.8)]',
         variant === 'secondary' &&
            'bg-[var(--ob-surface-2)] text-white hover:bg-[var(--ob-surface-3)]',
         variant === 'outline' &&
            'bg-transparent text-white border border-[var(--ob-border-strong)] hover:border-white/50',
         variant === 'ghost' &&
            'bg-transparent text-[var(--ob-text-2)] hover:text-white hover:bg-[var(--ob-surface-2)]',
         disabled &&
            '!bg-[var(--ob-surface-2)] !text-[var(--ob-text-3)] !shadow-none cursor-not-allowed pointer-events-none',
         full && 'w-full',
         className
      )}
   >
      {children}
   </button>
)

export const LinkButton = ({
   children,
   onClick,
   className,
   testId,
   disabled,
}: {
   children: ReactNode
   onClick: () => void
   className?: string
   testId?: string
   disabled?: boolean
}) => (
   <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
         'text-[var(--ob-light)] text-[14px] font-semibold hover:underline disabled:opacity-50 disabled:no-underline',
         className
      )}
   >
      {children}
   </button>
)

/* ---------- Inputs ---------- */

const fieldBase =
   'w-full rounded-2xl border bg-[var(--ob-surface)] text-white transition-colors focus:outline-none focus:border-[var(--ob-primary)] focus:ring-4 focus:ring-[var(--ob-primary-tint)]'

export const Input = ({
   value,
   onChange,
   placeholder,
   label,
   hint,
   error,
   success,
   type = 'text',
   autoFocus,
   onEnter,
   onBlur,
   testId,
   leading,
   trailing,
   size = 'md',
   className,
   align,
   inputMode,
   maxLength,
   disabled,
}: {
   value: string
   onChange: (v: string) => void
   placeholder?: string
   label?: ReactNode
   hint?: ReactNode
   error?: string | null
   /** A green state with a message, e.g. a verified key. */
   success?: ReactNode
   type?: string
   autoFocus?: boolean
   onEnter?: () => void
   onBlur?: () => void
   testId?: string
   leading?: ReactNode
   trailing?: ReactNode
   size?: 'md' | 'lg'
   className?: string
   align?: 'center'
   inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'decimal'
   maxLength?: number
   disabled?: boolean
}) => (
   <label className={cn('block', className)}>
      {label && (
         <span className="block text-[13px] font-medium text-[var(--ob-text-2)] mb-1.5">
            {label}
         </span>
      )}
      <span className="relative block">
         {leading && (
            <span
               className={cn(
                  'absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none',
                  success ? 'text-[var(--ob-good)]' : 'text-[var(--ob-text-3)]'
               )}
            >
               {leading}
            </span>
         )}
         <input
            type={type}
            value={value}
            autoFocus={autoFocus}
            placeholder={placeholder}
            inputMode={inputMode}
            maxLength={maxLength}
            disabled={disabled}
            onChange={(e) => onChange(e.currentTarget.value)}
            onBlur={onBlur}
            onKeyDown={(e) => {
               if (e.key === 'Enter' && onEnter) onEnter()
            }}
            data-testid={testId}
            className={cn(
               fieldBase,
               success
                  ? 'border-[var(--ob-good)] bg-[var(--ob-good-tint)]'
                  : error
                    ? 'border-[var(--ob-bad)]'
                    : 'border-[var(--ob-border)] hover:border-[var(--ob-border-strong)]',
               size === 'md' ? 'h-12 text-[15px]' : 'h-14 text-[17px]',
               leading ? 'pl-11' : 'pl-4',
               trailing || success ? 'pr-28' : 'pr-4',
               align === 'center' && 'text-center',
               disabled && 'opacity-60'
            )}
         />
         {(trailing || success) && (
            <span
               className={cn(
                  'absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-semibold',
                  success ? 'text-[var(--ob-good)]' : 'text-[var(--ob-text-3)]'
               )}
            >
               {success || trailing}
            </span>
         )}
      </span>
      {(hint || error) && (
         <span
            className={cn(
               'block text-[12px] mt-1.5',
               error ? 'text-[var(--ob-bad)]' : 'text-[var(--ob-text-3)]'
            )}
            data-testid={testId ? `${testId}-message` : undefined}
         >
            {error || hint}
         </span>
      )}
   </label>
)

export const Select = ({
   value,
   onChange,
   options,
   label,
   placeholder = 'Choose',
   testId,
   className,
   error,
}: {
   value: string
   onChange: (v: string) => void
   options: { value: string; label: string }[] | string[]
   label?: ReactNode
   placeholder?: string
   testId?: string
   className?: string
   error?: string | null
}) => (
   <label className={cn('block', className)}>
      {label && (
         <span className="block text-[13px] font-medium text-[var(--ob-text-2)] mb-1.5">
            {label}
         </span>
      )}
      <span className="relative block">
         <select
            value={value}
            onChange={(e) => onChange(e.currentTarget.value)}
            data-testid={testId}
            className={cn(
               fieldBase,
               'h-12 appearance-none pl-4 pr-10 text-[15px]',
               error
                  ? 'border-[var(--ob-bad)]'
                  : 'border-[var(--ob-border)] hover:border-[var(--ob-border-strong)]',
               !value && 'text-[var(--ob-text-3)]'
            )}
         >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((o) => {
               const v = typeof o === 'string' ? o : o.value
               const l = typeof o === 'string' ? o : o.label
               return (
                  <option key={v} value={v}>
                     {l}
                  </option>
               )
            })}
         </select>
         <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--ob-text-3)]">
            <svg
               width="14"
               height="14"
               viewBox="0 0 24 24"
               fill="none"
               stroke="currentColor"
               strokeWidth="2.2"
               strokeLinecap="round"
            >
               <path d="M6 9l6 6 6-6" />
            </svg>
         </span>
      </span>
      {error && (
         <span className="block text-[12px] mt-1.5 text-[var(--ob-bad)]">
            {error}
         </span>
      )}
   </label>
)

/** A label above a control that is not an Input (search boxes, groups). */
export const FieldLabel = ({ children }: { children: ReactNode }) => (
   <span className="block text-[13px] font-medium text-[var(--ob-text-2)] mb-1.5">
      {children}
   </span>
)

/* ---------- Chips: colour by kind ---------- */

export type TChipKind = 'stage' | 'tag' | 'source' | 'pond'

export const Chip = ({
   children,
   onRemove,
   onAdd,
   kind,
   testId,
   size = 'md',
}: {
   children: ReactNode
   onRemove?: () => void
   onAdd?: () => void
   kind?: TChipKind | string
   testId?: string
   size?: 'md' | 'lg'
}) => {
   const tone =
      kind === 'stage'
         ? 'bg-[var(--ob-stage-tint)] text-[var(--ob-stage)]'
         : kind === 'tag'
           ? 'bg-[var(--ob-tag-tint)] text-[var(--ob-tag)]'
           : kind === 'source'
             ? 'bg-[var(--ob-source-tint)] text-[var(--ob-source)]'
             : kind === 'pond'
               ? 'bg-[var(--ob-pond-tint)] text-[var(--ob-pond)]'
               : 'bg-[var(--ob-surface-2)] text-white'
   const base = cn(
      'inline-flex items-center gap-1.5 rounded-full font-medium leading-none transition-colors',
      size === 'md' ? 'px-3 h-9 text-[13px]' : 'px-4 h-11 text-[15px]'
   )
   if (onAdd)
      return (
         <button
            type="button"
            onClick={onAdd}
            data-testid={testId}
            className={cn(
               base,
               'border border-dashed border-[var(--ob-border-strong)] bg-transparent text-[var(--ob-text-2)] hover:border-[var(--ob-light)] hover:text-[var(--ob-light)]'
            )}
         >
            <span className="font-semibold">+</span>
            {children}
            {kind && (
               <span className="text-[10px] uppercase tracking-wide opacity-60">
                  {kind}
               </span>
            )}
         </button>
      )
   return (
      <span data-testid={testId} className={cn(base, tone)}>
         {children}
         {kind && (kind === 'stage' || kind === 'tag') && (
            <span className="text-[10px] uppercase tracking-wide opacity-60">
               {kind}
            </span>
         )}
         {onRemove && (
            <button
               type="button"
               aria-label={`Remove ${typeof children === 'string' ? children : ''}`}
               onClick={onRemove}
               className="ml-0.5 -mr-1 w-5 h-5 rounded-full opacity-60 hover:opacity-100 hover:bg-white/10 flex items-center justify-center text-[14px] leading-none"
            >
               ×
            </button>
         )}
      </span>
   )
}

/* ---------- Tiles, radios, cards ---------- */

export const Tile = ({
   selected,
   onClick,
   children,
   className,
   testId,
   role = 'radio',
   disabled,
}: {
   selected: boolean
   onClick: () => void
   children: ReactNode
   className?: string
   testId?: string
   role?: 'radio' | 'checkbox' | 'button'
   disabled?: boolean
}) => (
   <button
      type="button"
      role={role}
      aria-checked={role === 'button' ? undefined : selected}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
         'text-left rounded-2xl border bg-[var(--ob-surface)] p-4 transition-all w-full',
         selected
            ? 'border-[var(--ob-primary)] ring-4 ring-[var(--ob-primary-tint)]'
            : 'border-[var(--ob-border)] hover:border-[var(--ob-border-strong)]',
         disabled && 'opacity-40 cursor-not-allowed',
         className
      )}
   >
      {children}
   </button>
)

export const Radio = ({ on }: { on: boolean }) => (
   <span
      className={cn(
         'flex shrink-0 items-center justify-center w-5 h-5 rounded-full border-2 transition-colors',
         on ? 'border-[var(--ob-light)]' : 'border-[var(--ob-border-strong)]'
      )}
   >
      {on && <span className="w-2.5 h-2.5 rounded-full bg-[var(--ob-light)]" />}
   </span>
)

export const Checkbox = ({
   on,
   label,
   onChange,
   testId,
}: {
   on: boolean
   label: ReactNode
   onChange: (v: boolean) => void
   testId?: string
}) => (
   <label className="flex items-start gap-3 cursor-pointer text-[13px] text-[var(--ob-text-2)] leading-5">
      <input
         type="checkbox"
         className="sr-only"
         checked={on}
         onChange={(e) => onChange(e.currentTarget.checked)}
         data-testid={testId}
      />
      <span
         className={cn(
            'mt-0.5 flex shrink-0 items-center justify-center w-5 h-5 rounded-md border transition-colors',
            on
               ? 'bg-[var(--ob-primary)] border-[var(--ob-primary)] text-white'
               : 'border-[var(--ob-border-strong)] bg-[var(--ob-surface)]'
         )}
      >
         {on && <Icon name="check" size={13} />}
      </span>
      <span>{label}</span>
   </label>
)

/** A stacked card of rows. */
export const RowCard = ({
   children,
   className,
   testId,
}: {
   children: ReactNode
   className?: string
   testId?: string
}) => (
   <div
      className={cn(
         'rounded-[20px] border border-[var(--ob-border)] bg-[var(--ob-surface)] divide-y divide-[var(--ob-border)] overflow-hidden',
         className
      )}
      data-testid={testId}
   >
      {children}
   </div>
)
export const Row = ({
   children,
   className,
}: {
   children: ReactNode
   className?: string
}) => (
   <div className={cn('flex items-center gap-4 px-5 min-h-[72px]', className)}>
      {children}
   </div>
)

export const IconDot = ({
   name,
   tone = 'grey',
   size = 40,
}: {
   name: string
   tone?: 'grey' | 'purple' | 'green' | 'warn'
   size?: number
}) => (
   <span
      className={cn(
         'flex shrink-0 items-center justify-center rounded-full',
         tone === 'purple' &&
            'bg-[var(--ob-primary-tint)] text-[var(--ob-light)]',
         tone === 'green' && 'bg-[var(--ob-good-tint)] text-[var(--ob-good)]',
         tone === 'warn' && 'bg-[var(--ob-warn-tint)] text-[var(--ob-warn)]',
         tone === 'grey' && 'bg-[var(--ob-surface-2)] text-white'
      )}
      style={{ width: size, height: size }}
   >
      <Icon name={name} size={Math.round(size * 0.42)} />
   </span>
)

const HUES = [262, 160, 28, 290, 340, 190]
export const Avatar = ({
   name,
   size = 36,
}: {
   name: string
   size?: number
}) => {
   const initials = (name || '?')
      .split(' ')
      .filter(Boolean)
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
   const h = HUES[(name || 'a').charCodeAt(0) % HUES.length]
   return (
      <span
         className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold"
         style={{
            width: size,
            height: size,
            fontSize: size * 0.36,
            background: `hsl(${h} 45% 24%)`,
            color: `hsl(${h} 90% 84%)`,
         }}
      >
         {initials}
      </span>
   )
}

export const Skeleton = ({ className }: { className?: string }) => (
   <div
      className={cn(
         'animate-pulse rounded-xl bg-[var(--ob-surface-2)]',
         className
      )}
      aria-hidden
   />
)

export const Spinner = ({ size = 16 }: { size?: number }) => (
   <svg
      className="animate-spin"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
   >
      <circle
         cx="12"
         cy="12"
         r="9"
         stroke="currentColor"
         strokeOpacity="0.25"
         strokeWidth="3"
      />
      <path
         d="M21 12a9 9 0 0 0-9-9"
         stroke="currentColor"
         strokeWidth="3"
         strokeLinecap="round"
      />
   </svg>
)

/* ---------- Search + pick ---------- */

export interface IPickItem {
   id: string
   label: string
   sub?: string
   kind?: string
}

export const SearchPick = ({
   items,
   onPick,
   placeholder,
   emptyLabel = 'No match',
   testId,
   renderItem,
   size = 'md',
   loading,
   onQuery,
   keepOpen,
}: {
   items: IPickItem[]
   onPick: (id: string) => void
   placeholder: string
   emptyLabel?: string
   testId?: string
   renderItem?: (item: IPickItem) => ReactNode
   size?: 'md' | 'lg'
   loading?: boolean
   /** Called with the typed text, for callers that search a backend. */
   onQuery?: (q: string) => void
   /**
    * Picking leaves the list open with the typed words kept, so several
    * entries can be picked in a row. Escape or a click elsewhere closes it.
    */
   keepOpen?: boolean
}) => {
   const [q, setQ] = useState('')
   const [open, setOpen] = useState(false)
   const [hi, setHi] = useState(0)
   const ref = useRef<HTMLDivElement>(null)
   const inputRef = useRef<HTMLInputElement>(null)
   const results = useMemo(() => {
      const s = q.trim().toLowerCase()
      const list =
         s && !onQuery
            ? items.filter((i) =>
                 (i.label + ' ' + (i.sub || '')).toLowerCase().includes(s)
              )
            : items
      return list.slice(0, 8)
   }, [q, items, onQuery])
   useEffect(() => {
      const onDoc = (e: MouseEvent) => {
         if (ref.current && !ref.current.contains(e.target as Node))
            setOpen(false)
      }
      document.addEventListener('mousedown', onDoc)
      return () => document.removeEventListener('mousedown', onDoc)
   }, [])
   const pick = (id: string) => {
      onPick(id)
      if (keepOpen) {
         // The picked entry leaves the list; keep the highlight in range and
         // the focus in the box so the next pick is one more click or Enter.
         setHi((h) => Math.max(0, Math.min(h, results.length - 2)))
         inputRef.current?.focus()
         return
      }
      setQ('')
      onQuery?.('')
      setHi(0)
      setOpen(false)
   }
   return (
      <div ref={ref} className="relative">
         <input
            ref={inputRef}
            value={q}
            placeholder={placeholder}
            data-testid={testId}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
               setQ(e.currentTarget.value)
               onQuery?.(e.currentTarget.value)
               setOpen(true)
               setHi(0)
            }}
            onKeyDown={(e) => {
               if (e.key === 'ArrowDown')
                  setHi((h) => Math.min(results.length - 1, h + 1))
               if (e.key === 'ArrowUp') setHi((h) => Math.max(0, h - 1))
               if (e.key === 'Enter' && results[hi]) pick(results[hi].id)
               if (e.key === 'Escape') setOpen(false)
            }}
            className={cn(
               fieldBase,
               'border-[var(--ob-border)] hover:border-[var(--ob-border-strong)] pl-11 pr-4',
               size === 'md' ? 'h-12 text-[15px]' : 'h-14 text-[17px]'
            )}
         />
         <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ob-text-3)]">
            {loading ? <Spinner size={16} /> : <Icon name="search" size={16} />}
         </span>
         {open && (
            <div
               className="absolute z-30 left-0 right-0 mt-1.5 rounded-2xl border border-[var(--ob-border)] bg-[var(--ob-panel)] shadow-[var(--ob-shadow)] p-1.5 max-h-64 overflow-auto ob-scroll"
               data-testid={testId ? `${testId}-menu` : undefined}
            >
               {results.length === 0 ? (
                  <p className="text-[14px] text-[var(--ob-text-3)] px-3 py-2">
                     {loading ? 'Searching' : emptyLabel}
                  </p>
               ) : (
                  results.map((r, i) => (
                     <button
                        key={r.id}
                        type="button"
                        onMouseEnter={() => setHi(i)}
                        onMouseDown={(e) => {
                           // Keep the focus in the box: a click on an entry
                           // must not blur the input and close the list.
                           if (keepOpen) e.preventDefault()
                        }}
                        onClick={() => pick(r.id)}
                        className={cn(
                           'w-full text-left rounded-xl px-3 py-2 text-[14px] flex items-center gap-3',
                           i === hi ? 'bg-[var(--ob-primary-tint)]' : ''
                        )}
                        data-testid={testId ? `${testId}-option` : undefined}
                     >
                        {renderItem ? (
                           renderItem(r)
                        ) : (
                           <>
                              <span className="flex-1 min-w-0 truncate">
                                 {r.label}
                              </span>
                              {r.sub && (
                                 <span className="text-[12px] text-[var(--ob-text-3)] truncate">
                                    {r.sub}
                                 </span>
                              )}
                              {r.kind && (
                                 <span
                                    className={cn(
                                       'text-[10px] uppercase tracking-wide px-1.5 h-5 inline-flex items-center rounded-full',
                                       r.kind === 'stage'
                                          ? 'bg-[var(--ob-stage-tint)] text-[var(--ob-stage)]'
                                          : 'bg-[var(--ob-tag-tint)] text-[var(--ob-tag)]'
                                    )}
                                 >
                                    {r.kind}
                                 </span>
                              )}
                           </>
                        )}
                     </button>
                  ))
               )}
               {keepOpen && results.length > 0 && (
                  <p
                     className="px-3 pt-2 pb-1 text-[11px] text-[var(--ob-text-3)] border-t border-[var(--ob-border)] mt-1"
                     data-testid={testId ? `${testId}-hint` : undefined}
                  >
                     Pick as many as you need. Esc closes the list.
                  </p>
               )}
            </div>
         )}
      </div>
   )
}

/** An inline error line, in the backend's words when it has any. */
export const ErrorLine = ({
   children,
   testId,
}: {
   children: ReactNode
   testId?: string
}) => (
   <p
      className="flex items-start gap-2 text-[13px] text-[var(--ob-bad)] mt-3"
      role="alert"
      data-testid={testId}
   >
      <span className="mt-0.5 shrink-0">
         <Icon name="alert" size={14} />
      </span>
      <span>{children}</span>
   </p>
)
